import { getStreamDisplayName, getStreamStatus } from 'src/utils/leadStreams';

import { readDeliverySeen, writeDeliverySeen } from './deliverySeen';

import type { Stream } from 'src/services/LeadStreamsService/agent';
import type { AIManagerConversation } from 'src/services/AIManagersService';

export type ReviewQueue = { managerId: string; name: string; conversations: AIManagerConversation[] };
export type CompanionEvent = {
    id: string;
    version: string;
    kind: 'review' | 'ended' | 'delivery' | 'notifications';
    title: string;
    subject: string;
    path: string;
    streamId?: number;
    delivered?: number;
};
export type EventMemory = {
    delivered: Record<string, number>;
    observed: Record<string, number>;
    seen: Record<string, string>;
};
export const emptyEventMemory = (): EventMemory => ({ delivered: {}, observed: {}, seen: {} });
export const eventStorageKey = (userId: number) => `leadsmania:companion-events:${userId}`;

function mergeDeliverySeen(memory: EventMemory, counters: Record<string, number>): EventMemory {
    const result = { ...memory, delivered: { ...memory.delivered }, observed: { ...memory.observed } };
    for (const [id, count] of Object.entries(counters)) {
        result.delivered[id] = Math.max(result.delivered[id] ?? 0, count);
        result.observed[id] = Math.max(result.observed[id] ?? 0, result.delivered[id]);
    }
    return result;
}

export function readEventMemory(userId: number): EventMemory {
    const result = emptyEventMemory();
    try {
        const raw = JSON.parse(localStorage.getItem(eventStorageKey(userId)) || '{}');
        for (const field of ['delivered', 'observed'] as const) {
            for (const [key, value] of Object.entries(raw?.[field] || {}).slice(0, 500))
                if (/^\d+$/.test(key) && Number.isSafeInteger(value) && Number(value) >= 0)
                    result[field][key] = Number(value);
        }
        for (const [key, value] of Object.entries(raw?.seen || {}).slice(0, 500))
            if (
                (/^(ended|review):\d+$/.test(key) || key === 'notifications:telegram') &&
                typeof value === 'string' &&
                value.length <= 20000
            )
                result.seen[key] = value;
    } catch {
        /* A broken legacy snapshot does not erase delivery watermarks. */
    }
    return mergeDeliverySeen(result, readDeliverySeen(userId));
}

export function writeEventMemory(userId: number, memory: EventMemory): EventMemory {
    let next = memory;
    try {
        next = mergeDeliverySeen(memory, writeDeliverySeen(userId, memory.delivered));
        localStorage.setItem(eventStorageKey(userId), JSON.stringify(next));
    } catch {
        // In-memory acknowledgements still work when browser storage is unavailable.
    }
    return next;
}

const plural = (count: number, one: string, few: string, many: string) =>
    count % 100 >= 11 && count % 100 <= 14
        ? many
        : count % 10 === 1
        ? one
        : count % 10 >= 2 && count % 10 <= 4
        ? few
        : many;

export function buildCompanionEvents(
    memory: EventMemory,
    streams?: Stream[],
    queues?: ReviewQueue[],
    telegram: { enabled?: boolean; connected?: boolean } = {}
) {
    const next: EventMemory = {
        delivered: { ...memory.delivered },
        observed: { ...memory.observed },
        seen: { ...memory.seen },
    };
    const events: CompanionEvent[] = [];
    if (streams) {
        const ids = new Set(streams.map((s) => String(s.id)));
        for (const id of Object.keys(next.observed))
            if (!ids.has(id)) {
                delete next.observed[id];
                delete next.delivered[id];
            }
        for (const s of streams) {
            if (Number.isSafeInteger(s.approved_deliveries) && Number(s.approved_deliveries) >= 0) {
                const count = Number(s.approved_deliveries);
                // Seed historical deliveries once. Later visits can identify deliveries since the last view.
                next.delivered[s.id] ??= count;
                next.observed[s.id] = Math.max(count, next.observed[s.id] ?? count);
                const added = next.observed[s.id] - next.delivered[s.id];
                if (added > 0)
                    events.push({
                        id: `delivery:${s.id}`,
                        version: String(next.observed[s.id]),
                        kind: 'delivery',
                        title: `${added} ${plural(added, 'новый лид', 'новых лида', 'новых лидов')}`,
                        subject: getStreamDisplayName(s),
                        path: `/feed?stream=${s.id}&from=companion`,
                        streamId: s.id,
                        delivered: next.observed[s.id],
                    });
            }
            if (getStreamStatus(s).key === 'ended')
                events.push({
                    id: `ended:${s.id}`,
                    version: `${s.package_size || 0}:${s.approved_deliveries || 0}:${s.access_expires_at || ''}`,
                    kind: 'ended',
                    title: s.active_access_offer_kind ? 'Доступ закончился' : 'Пакет закончился',
                    subject: getStreamDisplayName(s),
                    path: `/leadstream/${s.id}#checkout`,
                });
            else delete next.seen[`ended:${s.id}`];
        }
        for (const key of Object.keys(next.seen))
            if (key.startsWith('ended:') && !ids.has(key.slice(6))) delete next.seen[key];
        if (
            streams.some((s) => s.is_active && getStreamStatus(s).key === 'active') &&
            (telegram.enabled === false || telegram.connected === false)
        )
            events.push({
                id: 'notifications:telegram',
                version: 'disabled',
                kind: 'notifications',
                title: 'Включить уведомления в Telegram',
                subject: 'Получайте новые лиды сразу',
                path: '/notifications',
            });
        else if (telegram.enabled === true && telegram.connected === true) delete next.seen['notifications:telegram'];
    }
    for (const queue of queues || []) {
        const pending = queue.conversations.filter((c) => c.status === 'human_review_required');
        if (!pending.length) {
            delete next.seen[`review:${queue.managerId}`];
            continue;
        }
        // Count alone misses a different conversation arriving while another leaves the queue.
        const version = pending
            .map((c) => `${c.id}:${c.messageCount}:${c.outboundCount}:${c.latestOutboundStatus || ''}`)
            .sort()
            .join('|');
        events.push({
            id: `review:${queue.managerId}`,
            version,
            kind: 'review',
            title: `${pending.length} ${plural(pending.length, 'диалог', 'диалога', 'диалогов')} на проверку`,
            subject: queue.name,
            path: `/sales-funnel?managerId=${queue.managerId}&review=1`,
        });
    }
    const order = { review: 0, ended: 1, delivery: 2, notifications: 3 };
    events.sort((a, b) => order[a.kind] - order[b.kind] || a.id.localeCompare(b.id));
    return { memory: next, events, unread: events.filter((e) => next.seen[e.id] !== e.version) };
}

export function acknowledgeEvent(memory: EventMemory, event: CompanionEvent): EventMemory {
    if (event.kind === 'delivery' && event.streamId && event.delivered !== undefined)
        return {
            ...memory,
            delivered: {
                ...memory.delivered,
                [event.streamId]: Math.max(memory.delivered[event.streamId] || 0, event.delivered),
            },
        };
    return { ...memory, seen: { ...memory.seen, [event.id]: event.version } };
}

// Saved chat actions are untrusted; only these exact internal destinations are supported.
export function allowedCompanionPath(path: string) {
    return (
        /^\/(feed|notifications|leadstream(?:\/(?:\d+|s-[0-9a-z]{7}))?|ai-managers\/new)$/.test(path) ||
        /^\/feed\?stream=\d+(?:&from=companion)?$/.test(path) ||
        /^\/leadstream\/(?:\d+|s-[0-9a-z]{7})#(?:checkout|rules)$/.test(path) ||
        /^\/sales-funnel\?managerId=\d+&review=1$/.test(path)
    );
}
